import * as Sentry from "@sentry/browser";

const SENTRY_HOST = window.ENV?.SENTRY_HOST;
const FRONTEND_SENTRY_DSN = window.ENV?.FRONTEND_SENTRY_DSN;

const CUSTOMER_ENV = window.ENV?.CUSTOMER_ENV;
const REPLAYS_SESSION_SAMPLE_RATE = window.ENV?.REPLAYS_SESSION_SAMPLE_RATE;
const REPLAY_ON_ERROR_SAMPLE_RATE = window.ENV?.REPLAY_ON_ERROR_SAMPLE_RATE;
const TRACES_SAMPLE_RATE = window.ENV?.TRACES_SAMPLE_RATE;
const SEND_DEFAULT_PII = window.ENV?.SEND_DEFAULT_PII;
const NO_SENTRY_ACCESS = window.ENV?.NO_SENTRY_ACCESS;
const FRONTEND_SENTRY_PROJECT_ID = window.ENV?.FRONTEND_SENTRY_PROJECT_ID;

const nuclearn_version = window.ENV?.NUCLEARN_VERSION;
const frontendVersion = import.meta.env.VITE_GIT_BRANCH || nuclearn_version;

let sentryInitialized = false;

export const initializeSentry = () => {
  if (NO_SENTRY_ACCESS) {
    console.debug("Sentry initialization skipped: NO_SENTRY_ACCESS is true");
    return;
  }

  // Prevent multiple initializations
  if (sentryInitialized) {
    console.debug("Sentry already initialized, skipping");
    return;
  }

  // Check if Sentry is already initialized
  if (Sentry.getClient()) {
    console.debug("Sentry client already exists, skipping initialization");
    sentryInitialized = true;
    return;
  }

  try {
    Sentry.init({
      dsn: `https://${FRONTEND_SENTRY_DSN}@${SENTRY_HOST}/${FRONTEND_SENTRY_PROJECT_ID}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      replaysSessionSampleRate: Number(REPLAYS_SESSION_SAMPLE_RATE),
      replaysOnErrorSampleRate: Number(REPLAY_ON_ERROR_SAMPLE_RATE),
      tracesSampleRate: Number(TRACES_SAMPLE_RATE),
      sendDefaultPii: Boolean(SEND_DEFAULT_PII),
      environment: import.meta.env.PROD ? CUSTOMER_ENV : import.meta.env.MODE,
      release: `frontend@${frontendVersion}`,
      // Unified beforeSend handler merging prior duplicated definitions.
      // (1) Drops Cloudflare 524 timeout network noise.
      // (2) Drops expected 401 auth/login failures during normal login flow.
      beforeSend(event, hint) {
        try {
          const original = hint?.originalException as any;

          // Attempt to derive HTTP status from several potential locations
          const derivedStatus =
            original?.response?.status ??
            original?.status ??
            Number(event.tags?.["http.status_code"]) ??
            Number(event.extra?.status);

          // 1. Cloudflare 524 filtering
          if (derivedStatus === 524) {
            console.warn("Dropping Sentry event for Cloudflare 524 timeout");
            return null; // silence
          }

          // 2. Suppress noisy 401s during login sequence for Axios errors
          const isAxios =
            original &&
            (original.isAxiosError || original.name === "AxiosError");
          if (isAxios && derivedStatus === 401) {
            const url: string | undefined =
              original?.config?.url || original?.response?.config?.url;
            const isLoginApi = !!(url && /\/auth\/login\/?(?:$|\?)/i.test(url));
            const isOnLoginRoute = /\/login$/.test(window.location.pathname);
            if (isLoginApi || isOnLoginRoute) {
              return null; // silence expected auth failures
            }
          }
        } catch (filterError) {
          // Fail open: if any filtering logic throws, allow event through
          console.debug("Sentry beforeSend unified filter failed", filterError);
        }
        return event; // default: send
      },
    });

    Sentry.setTag("customer_env", CUSTOMER_ENV);
    sentryInitialized = true;
    console.debug("Sentry initialized successfully");
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
  }
};

export const setSentryUser = () => {
  if (NO_SENTRY_ACCESS || !Sentry.getClient()) return;

  const user = localStorage.getItem("nuclearn_user");
  if (user && user !== "undefined") {
    let jsonUser = JSON.parse(user);

    Sentry.setUser({
      id: jsonUser.id,
      email: jsonUser.user_display_name,
      username: jsonUser.user_display_name,
    });
  } else {
    Sentry.setUser(null);
  }
};
